import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  buildCommissionRecalculationPayload,
  buildEmptyCommissionRecalculationForm,
  validateCommissionRecalculationForm,
} from '../model/commission-recalculation.model.js';

function errorLabel(t, errorCode) {
  if (!errorCode) {
    return '';
  }

  if (errorCode === 'required') {
    return t('commissionRecalculation.requiredDate');
  }

  return t('commissionRecalculation.invalidDateRange');
}

function CommissionRecalculationModal({
  isOpen,
  isSaving,
  title,
  scopeLabel,
  error,
  onSubmit,
  onClose,
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState(buildEmptyCommissionRecalculationForm);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      setForm(buildEmptyCommissionRecalculationForm());
      setErrors({});
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const updateField = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
    setErrors({});
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const nextErrors = validateCommissionRecalculationForm(form);
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    onSubmit(buildCommissionRecalculationPayload(form));
  };

  const showDateRange = form.dateMode === 'date_range';

  return (
    <>
      <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true" aria-labelledby="commission-recalculation-modal-title">
        <div className="modal-dialog modal-dialog-centered">
          <form className="modal-content" onSubmit={handleSubmit}>
            <div className="modal-header">
              <div>
                <h5 className="modal-title" id="commission-recalculation-modal-title">
                  {title}
                </h5>
                {scopeLabel ? <div className="text-muted small">{scopeLabel}</div> : null}
              </div>
              <button type="button" className="btn-close" aria-label={t('common.close')} onClick={onClose} disabled={isSaving} />
            </div>

            <div className="modal-body">
              {error ? <div className="alert alert-danger">{error}</div> : null}

              <div className="alert alert-warning" role="note">
                {t('commissionRecalculation.warning')}
              </div>

              <div className="mb-3">
                <label className="form-label" htmlFor="commission-recalculation-date-mode">
                  {t('commissionRecalculation.dateMode')}
                </label>
                <select
                  id="commission-recalculation-date-mode"
                  className="form-select"
                  value={form.dateMode}
                  onChange={(event) => updateField('dateMode', event.target.value)}
                  disabled={isSaving}
                >
                  <option value="all_history">{t('commissionRecalculation.allHistory')}</option>
                  <option value="date_range">{t('commissionRecalculation.dateRange')}</option>
                </select>
              </div>

              {showDateRange ? (
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label" htmlFor="commission-recalculation-date-from">
                      {t('commissionRecalculation.dateFrom')}
                    </label>
                    <input
                      id="commission-recalculation-date-from"
                      type="date"
                      className={`form-control ${errors.dateFrom ? 'is-invalid' : ''}`}
                      value={form.dateFrom}
                      onChange={(event) => updateField('dateFrom', event.target.value)}
                      disabled={isSaving}
                      aria-invalid={errors.dateFrom ? 'true' : 'false'}
                      aria-describedby={errors.dateFrom ? 'commission-recalculation-date-from-error' : undefined}
                    />
                    {errors.dateFrom ? (
                      <div id="commission-recalculation-date-from-error" className="invalid-feedback">
                        {errorLabel(t, errors.dateFrom)}
                      </div>
                    ) : null}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label" htmlFor="commission-recalculation-date-to">
                      {t('commissionRecalculation.dateTo')}
                    </label>
                    <input
                      id="commission-recalculation-date-to"
                      type="date"
                      className={`form-control ${errors.dateTo ? 'is-invalid' : ''}`}
                      value={form.dateTo}
                      onChange={(event) => updateField('dateTo', event.target.value)}
                      disabled={isSaving}
                      aria-invalid={errors.dateTo ? 'true' : 'false'}
                      aria-describedby={errors.dateTo ? 'commission-recalculation-date-to-error' : undefined}
                    />
                    {errors.dateTo ? (
                      <div id="commission-recalculation-date-to-error" className="invalid-feedback">
                        {errorLabel(t, errors.dateTo)}
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={isSaving}>
                {t('common.close')}
              </button>
              <button type="submit" className="btn btn-primary" disabled={isSaving}>
                {isSaving ? t('commissionRecalculation.saving') : t('commissionRecalculation.submit')}
              </button>
            </div>
          </form>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
}

export default CommissionRecalculationModal;
